import {
  apiErrorMessages,
  formatApiError,
} from '@efdigital/ll-xceed-v2/utils/application/api-error';
import type { Future, Handler, NextFn } from '@warp-drive/core/request';
import type {
  RequestContext,
  StructuredDataDocument,
} from '@warp-drive/core/types/request';

/**
 * Rewrites the message on a failed request so it says why it failed.
 *
 * Warp Drive's fetch handler throws with `message` set to
 * `[403 Forbidden] PATCH (cors) - https://api…/coachProfiles/123`, and puts the
 * response body — the part that says *why* — on `content`/`errors`. Every place
 * in the app that reports a failure reads `message`, so a coach saving their
 * profile got the request URL and the real reason ("LinkedIn Profile Link must
 * be a string") was only in the network tab.
 *
 * Fixing it here rather than at each call site means anything that already
 * shows, alerts or logs `error.message` shows the reason instead, including the
 * places that have no error handling of their own yet.
 *
 * The error object itself is reused rather than replaced: callers depend on
 * `status`, on `instanceof Error`, and on it being the `AggregateError` Warp
 * Drive built when the body carried an `errors` array. The original status line
 * moves to `requestLine`, since it locates the request in a way the reason
 * cannot.
 */

/** Marks an error as already rewritten, so a retry cannot double-process it. */
const REWRITTEN = Symbol('api-error-rewritten');

interface RewritableError {
  message?: string;
  status?: number;
  statusText?: string;
  isRequestError?: boolean;
  requestLine?: string;
  [REWRITTEN]?: boolean;
}

/**
 * What to say when the body explains nothing — an empty 500, an HTML error
 * page, a proxy timeout. Still better than the URL: it names what happened
 * without asking the reader to parse a request line.
 */
function statusPhrase(error: RewritableError): string {
  const { status, statusText } = error;

  // 401 and 403 are different problems with different remedies: a 401 is
  // almost always an expired session, which signing in again fixes, while a
  // 403 means the account genuinely lacks the right and retrying will not
  // help.
  if (status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 403) {
    return 'You do not have permission to do that.';
  }
  if (status === 404) {
    return 'That could not be found.';
  }
  if (status === 409) {
    return 'That conflicts with a change someone else made. Reload and try again.';
  }
  if (status && status >= 500) {
    return 'The server could not complete that request. Please try again.';
  }
  if (!status) {
    // No status at all means the request never got a response.
    return 'Could not reach the server. Check your connection and try again.';
  }

  return statusText
    ? `The request was rejected (${status} ${statusText}).`
    : `The request was rejected (${status}).`;
}

function rewrite(error: unknown): void {
  if (!error || typeof error !== 'object') {
    return;
  }

  const candidate = error as RewritableError;

  // Only touch request errors. An error the app threw itself is already
  // written for a person.
  if (!candidate.isRequestError || candidate[REWRITTEN]) {
    return;
  }

  const reasons = apiErrorMessages(error);
  const requestLine = candidate.message;

  // A 5xx is not the reader's problem to solve, and ll-api sends the raw
  // exception as the title on several handlers (`{ status: '500', title:
  // error.message }`), so its text is an internal detail rather than something
  // to act on. Say something generic and leave the specifics to the API logs -
  // the reasons are still logged below, and `requestLine` locates the request.
  const isServerFault = !candidate.status || candidate.status >= 500;

  try {
    candidate.requestLine = requestLine;
    // formatApiError does the joining, so several validation failures read as
    // several sentences here too. The server-fault check comes first because
    // it deliberately ignores whatever reasons the body carried.
    candidate.message = isServerFault
      ? statusPhrase(candidate)
      : formatApiError(error, statusPhrase(candidate));
    candidate[REWRITTEN] = true;
  } catch {
    // A frozen error is not worth failing the request over; the caller still
    // gets the original.
    return;
  }

  // One structured line per failure, with the parts a request line cannot
  // carry. Individual call sites console.error the error itself, which no
  // longer includes the URL.
  console.error('[api] request failed', {
    status: candidate.status,
    statusText: candidate.statusText,
    request: requestLine,
    reasons,
  });
}

export class ApiErrorHandler implements Handler {
  request<T = unknown>(
    context: RequestContext,
    next: NextFn<T>
  ): Promise<T | StructuredDataDocument<T>> | Future<T> {
    return next(context.request).catch((error: unknown) => {
      rewrite(error);
      throw error;
    });
  }
}
